import { useState, type FormEvent } from "react";
import { useListFounders, useCreateFounder, getListFoundersQueryKey, useListCompanies, getListCompaniesQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "wouter";
import { Plus, Users, ExternalLink, Mail, Loader2, Copy } from "lucide-react";
import InfoTip from "@/components/info-tip";
import ProgressLoader from "@/components/progress-loader";
import { useToast } from "@/hooks/use-toast";
import { useCohort } from "@/hooks/use-cohort";

const API_BASE = import.meta.env.BASE_URL;

function adminAuthHeaders() {
  const token = localStorage.getItem("admin_token") || "";
  return { "Content-Type": "application/json", "x-admin-token": token };
}

export default function Founders({ cohortSlug }: { cohortSlug?: string } = {}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [invitingId, setInvitingId] = useState<number | null>(null);
  const [inviteSent, setInviteSent] = useState<{ name: string; email: string } | null>(null);
  const [inviteFallback, setInviteFallback] = useState<{ name: string; url: string } | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { cohortId } = useCohort(cohortSlug);
  const { data: founders, isLoading } = useListFounders();
  const { data: companies } = useListCompanies({ cohortId }, { query: { enabled: !cohortSlug || !!cohortId, queryKey: getListCompaniesQueryKey({ cohortId }) } });
  const createMutation = useCreateFounder();

  const sendInvite = async (founder: { id: number; name: string; email: string }) => {
    if (invitingId) return;
    setInvitingId(founder.id);
    try {
      const r = await fetch(`${API_BASE}api/founders/${founder.id}/send-invite`, {
        method: "POST",
        headers: adminAuthHeaders(),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        // 401 = the ADMIN's own session token is missing/expired (NOT the
        // founder's onboarding token). The old toast surfaced the raw
        // "Invalid or expired token" which read like the email link was
        // broken — actively misleading. Say what's really wrong and send the
        // admin to re-login (command-center pages only check token presence,
        // not validity, so nothing else bounces them).
        if (r.status === 401) {
          toast({
            title: "Your admin session has expired",
            description: "Please log in again, then resend the onboarding email.",
            variant: "destructive",
          });
          localStorage.removeItem("admin_token");
          setTimeout(() => window.location.replace("/admin/login"), 1200);
          return;
        }
        // 502 = email failed but token minted; hand the admin the link to send manually.
        if (d.setupUrl) {
          setInviteFallback({ name: founder.name, url: d.setupUrl });
        } else {
          toast({ title: "Couldn't send onboarding email", description: d.error || "Please try again.", variant: "destructive" });
        }
        return;
      }
      setInviteSent({ name: founder.name, email: d.sentTo || founder.email });
    } catch (err: any) {
      toast({ title: "Couldn't send onboarding email", description: err?.message || "Network error.", variant: "destructive" });
    } finally {
      setInvitingId(null);
    }
  };

  const companyMap = new Map((companies ?? []).map(c => [c.id, c.name]));

  const handleCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    createMutation.mutate(
      {
        data: {
          companyId: parseInt(form.get("companyId") as string, 10),
          name: form.get("name") as string,
          email: form.get("email") as string,
          role: form.get("role") as string,
          bio: (form.get("bio") as string) || null,
          linkedinUrl: (form.get("linkedinUrl") as string) || null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListFoundersQueryKey() });
          setDialogOpen(false);
          toast({ title: "Founder added" });
        },
      }
    );
  };

  return (
    <div className="space-y-6" data-testid="page-founders">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-serif font-bold tracking-tight">Founders</h1>
            <InfoTip content="Every founder across all portfolio companies. Each founder has their own workspace where they submit weekly check-ins and approve what information gets shared with investors." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">All founders across portfolio companies</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-founder"><Plus className="w-4 h-4 mr-2" />Add Founder</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif">Add Founder</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Company</Label>
                <select name="companyId" className="w-full h-9 rounded-md border bg-background px-3 text-sm" required data-testid="select-founder-company">
                  <option value="">Select company</option>
                  {companies?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name</Label><Input name="name" required data-testid="input-founder-name" /></div>
                <div className="space-y-2"><Label>Email</Label><Input name="email" type="email" required data-testid="input-founder-email" /></div>
              </div>
              <div className="space-y-2"><Label>Role</Label><Input name="role" required data-testid="input-founder-role" /></div>
              <div className="space-y-2"><Label>Bio</Label><Textarea name="bio" data-testid="input-founder-bio" /></div>
              <div className="space-y-2"><Label>LinkedIn URL</Label><Input name="linkedinUrl" data-testid="input-founder-linkedin" /></div>
              <Button type="submit" className="w-full" disabled={createMutation.isPending} data-testid="button-submit-founder">
                {createMutation.isPending ? "Adding..." : "Add Founder"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-36" />)}
        </div>
      ) : founders && founders.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {founders.map((founder) => (
            <Card key={founder.id} data-testid={`card-founder-${founder.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium truncate">{founder.name}</h3>
                    <p className="text-xs text-muted-foreground">{founder.role}</p>
                  </div>
                </div>
                <Link href={cohortSlug ? `/c/${cohortSlug}/companies/${founder.companyId}` : `/companies/${founder.companyId}`}>
                  <Badge variant="outline" className="text-xs cursor-pointer hover:bg-accent mb-2" data-testid={`badge-company-${founder.companyId}`}>
                    {companyMap.get(founder.companyId) ?? `Company #${founder.companyId}`}
                  </Badge>
                </Link>
                {founder.bio && <p className="text-sm text-muted-foreground line-clamp-2 mt-2">{founder.bio}</p>}
                <div className="flex items-center gap-3 mt-3 pt-3 border-t">
                  <span className="text-xs text-muted-foreground truncate">{founder.email}</span>
                  {founder.linkedinUrl && (
                    <a href={founder.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-primary" data-testid={`link-linkedin-${founder.id}`}>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-3"
                  disabled={!!invitingId}
                  onClick={() => sendInvite({ id: founder.id, name: founder.name, email: founder.email })}
                  data-testid={`button-send-invite-${founder.id}`}
                >
                  {invitingId === founder.id ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Sending…</>
                  ) : (
                    <><Mail className="w-3.5 h-3.5 mr-2" />Send onboarding email</>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No founders yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add founders to your portfolio companies</p>
          </CardContent>
        </Card>
      )}

      {invitingId !== null && (
        <ProgressLoader
          active={true}
          title="Sending onboarding email"
          stages={["Minting secure setup link", "Composing the welcome email", "Handing off to Resend", "Confirming delivery"]}
          stageIntervalMs={1500}
        />
      )}

      <Dialog open={!!inviteSent} onOpenChange={(o) => !o && setInviteSent(null)}>
        <DialogContent data-testid="dialog-invite-sent">
          <DialogHeader><DialogTitle className="font-serif">Onboarding email sent</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {inviteSent?.name} will receive a Favcy Select welcome at{" "}
            <span className="font-medium text-foreground">{inviteSent?.email}</span> with a secure link to set their
            password and sign in to their cohort workspace. The link expires in 24 hours.
          </p>
          <Button className="w-full mt-2" onClick={() => setInviteSent(null)}>Done</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!inviteFallback} onOpenChange={(o) => !o && setInviteFallback(null)}>
        <DialogContent data-testid="dialog-invite-fallback">
          <DialogHeader><DialogTitle className="font-serif">Email didn't send — link still works</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            We couldn't deliver the email to {inviteFallback?.name}, but the setup link below is valid for 24 hours.
            Copy it and send it to them directly.
          </p>
          <div className="flex items-center gap-2 mt-2">
            <Input readOnly value={inviteFallback?.url ?? ""} className="text-xs" data-testid="input-invite-fallback-url" />
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (inviteFallback?.url) {
                  navigator.clipboard.writeText(inviteFallback.url);
                  toast({ title: "Link copied" });
                }
              }}
              data-testid="button-copy-invite-fallback"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <Button className="w-full mt-2" onClick={() => setInviteFallback(null)}>Done</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
